import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, TemplateRef, ViewChild, Output, EventEmitter, ElementRef } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { FullscreenOverlayContainer, Overlay, OverlayContainer, OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import { ViewContainerRef } from '@angular/core';
import { filter, take } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UzunBasDirective } from '@/src/service/fikoyt-ui/pipes/filoyt-uzun-bas.pipe';
import { FikoGrup } from '../../IFikoThreeJS/FikoGrup';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { TemplatePortal } from '@angular/cdk/portal';

/**
 * Tree-3D Selection Sync Interface
 * 3D viewport ile ürün ağacı arasında senkronizasyon
 */
export interface TreeSelectionState {
  /** 3D'den hover edilen objenin UUID'si */
  hoveredUuid: string | null;
  /** 3D'den seçilen objelerin UUID'leri */
  selectedUuids: Set<string>;
  /** Seçili objenin parent path UUID'leri */
  parentPathUuids: Set<string>;
}


@Component({
  selector: 'fikoyt-agac-item',
  templateUrl: './agac-item.component.html',
  styleUrls: ['./agac-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayModule, UzunBasDirective,],
  providers: [{ provide: OverlayContainer, useClass: FullscreenOverlayContainer }]
})
export class AgacItemComponent implements OnDestroy, AfterViewInit {
  dc: Subscription;
  /** */
  private _grup: FikoGrup | any;
  public get grup(): FikoGrup | any {
    return this._grup;
  }
  @Input() public set grup(v: FikoGrup | any) {
    this._grup = v;
    this.CD.detectChanges();
  }

  // ═══════════════════════════════════════════════════════════
  // 3D-TREE SYNC: Selection state from parent
  // ═══════════════════════════════════════════════════════════

  /** Selection state passed from parent (3D sync) */
  @Input() selectionState: TreeSelectionState | null = null;

  /** Event when tree item is hovered */
  @Output() treeHover = new EventEmitter<FikoGrup | null>();

  /** Event when tree item is clicked (for 3D sync) */
  @Output() treeSelect = new EventEmitter<{ grup: FikoGrup, ctrlKey: boolean }>();

  /** Sürükle-bırak ile yeniden sıralama (feature reorder) */
  @Output() treeReorder = new EventEmitter<{ kaynak: any, hedef: any }>();

  /** Ağaç arama filtresi (eşleşmeyen satırlar soluklaşır) */
  @Input() agacArama = '';

  // ── Inline yeniden adlandırma ──
  duzenlenenUuid: string | null = null;
  duzenleAd = '';
  /** Sürüklenen node (drag-reorder) — tüm instance'lar paylaşsın */
  static suruklenen: any = null;

  /** Arama terimi bu node adıyla eşleşiyor mu (Türkçe duyarsız). */
  aramaEslesme(grp: any): boolean {
    const q = (this.agacArama || '').trim().toLocaleLowerCase('tr');
    if (!q) return true;
    return (grp?.name || '').toLocaleLowerCase('tr').includes(q);
  }

  renameBaslat(grp: any): void {
    if (!grp) return;
    this.duzenlenenUuid = grp.uuid;
    this.duzenleAd = (grp.name || '').replace(/^◆ /, '');
    this.CD.detectChanges();
    setTimeout(() => { const el = this.elRef?.nativeElement?.querySelector('.fiko-ad-input'); el?.focus?.(); el?.select?.(); }, 0);
  }
  renameKaydet(grp: any): void {
    const ad = (this.duzenleAd || '').trim();
    if (grp && ad) {
      const onek = (grp.name || '').startsWith('◆ ') ? '◆ ' : '';
      grp.name = onek + ad;
      if (grp.userData?.['FikoArayuz']) grp.userData['FikoArayuz'].ad = grp.name;
      if (grp._taban !== undefined) grp._taban = ad;        // feature node: ozet eki korunur
      if (grp._bodyTaban !== undefined) grp._bodyTaban = ad; // body node
      try { grp.adDegistirildi?.(ad); } catch { }
    }
    this.duzenlenenUuid = null;
    this.CD.detectChanges();
    this.FikoYT.degisti.emit();
  }
  renameIptal(): void { this.duzenlenenUuid = null; this.CD.detectChanges(); }

  /** İsme çift-tık: feature/body → rename; sketch/datum → mevcut ciftTiklandi (eskiz düzenle/başlat korunur). */
  adCiftTik($event: any, grp: any): void {
    $event.preventDefault(); $event.stopPropagation();
    if (grp?.dtRename || !grp?.ciftTiklandi) this.renameBaslat(grp);
    else this.itemCiftTiklandi($event, grp);
  }

  // ── Drag-reorder ──
  dragBasla($event: DragEvent, grp: any): void {
    if (!grp?.siralanabilir) { $event.preventDefault(); return; }
    AgacItemComponent.suruklenen = grp;
    try { $event.dataTransfer?.setData('text/plain', grp.uuid || ''); $event.dataTransfer!.effectAllowed = 'move'; } catch { }
  }
  dragUstunde($event: DragEvent, grp: any): void {
    if (AgacItemComponent.suruklenen && grp?.siralanabilir && AgacItemComponent.suruklenen !== grp) { $event.preventDefault(); try { $event.dataTransfer!.dropEffect = 'move'; } catch { } }
  }
  birak($event: DragEvent, grp: any): void {
    $event.preventDefault();
    const kaynak = AgacItemComponent.suruklenen; AgacItemComponent.suruklenen = null;
    if (kaynak && grp && kaynak !== grp && grp.siralanabilir) this.treeReorder.emit({ kaynak, hedef: grp });
  }

  /** Check if this item is hovered from 3D */
  get is3DHovered(): boolean {
    if (!this.selectionState?.hoveredUuid || !this.grup) return false;
    return this.grup.uuid === this.selectionState.hoveredUuid;
  }

  /** Check if this item is selected from 3D */
  get is3DSelected(): boolean {
    if (!this.selectionState?.selectedUuids || !this.grup) return false;
    return this.selectionState.selectedUuids.has(this.grup.uuid);
  }

  /** Check if this item is parent of a selected item */
  get isParentOfSelected(): boolean {
    if (!this.selectionState?.parentPathUuids || !this.grup) return false;
    return this.selectionState.parentPathUuids.has(this.grup.uuid);
  }

  /** Compute CSS classes for tree item */
  get treeItemClasses(): { [key: string]: boolean } {
    return {
      'fiko-3d-hovered': this.is3DHovered,
      'fiko-3d-selected': this.is3DSelected,
      'fiko-parent-of-selected': this.isParentOfSelected
    };
  }

  // ═══════════════════════════════════════════════════════════

  /** */
  sub: Subscription;

  @ViewChild('itemMenu', { static: false }) itemMenu: TemplateRef<any>;

  overlayRef: OverlayRef | null;


  constructor(public FikoYT: FikoYTService, public CD: ChangeDetectorRef, public overlay: Overlay, public viewContainerRef: ViewContainerRef, public elRef: ElementRef) {

  }

  ngAfterViewInit(): void {
    this.dc = this.FikoYT.degisti.subscribe(event => {
      this.CD.detectChanges();
    })
    this.CD.detach();
  }

  ngOnDestroy(): void {
    if (this.dc)
      this.dc.unsubscribe();
  }


  /** Ağaç satırından görünürlük aç/kapa (CATIA/Fusion göz toggle). FikoGrup.visible çocukları da gizler. */
  gorunurlukToggle($event: Event, grp: any) {
    $event.stopPropagation();
    $event.preventDefault();
    grp.visible = !grp.visible;
    this.CD.detectChanges();
    this.FikoYT.degisti.emit();
  }

  /** Inline param değişimi → callback + lokal değer + tek noktadan CD/redraw. */
  paramDegisti(prm: any, deger: any) {
    prm.degeri = deger;
    prm.degisti?.(deger);
    this.CD.detectChanges();
  }

  /** f(x) chip tıklandı → ayrı f(x) editör dialogunu aç. */
  paramFormulDuzenle($event: any, prm: any) {
    $event.stopPropagation();
    prm?.knowledge?.fxAc?.(prm.paramId);
  }

  clickCount = 0;
  itemTiklandi($event: any, grup: any) {
    $event.preventDefault();

    var _this = this;
    this.clickCount++;
    setTimeout(() => {
      if (_this.clickCount == 1) {
        // 🎯 TEK KAYNAK: Tüm seçim mantığı selectFromTree'de
        // CTRL tuşu ile multi-select desteği
        this.treeSelect.emit({ grup: grup, ctrlKey: $event.ctrlKey });

        // Aktif item güncelle
        this.grup.sahne.anaGosterim.aktifItem = grup;
        this.CD.detectChanges();
        this.FikoYT.degisti.emit();

        // Özel tıklama callback'i
        //@ts-ignore
        if (grup.tiklandi)
          //@ts-ignore
          grup.tiklandi();
      } else if (_this.clickCount == 2) {
        //@ts-ignore
        if (grup.ciftTiklandi)
          //@ts-ignore
          grup.ciftTiklandi();
      }
      _this.clickCount = 0;
    }, 220)
  }

  itemCiftTiklandi($event: any, grup: any) {
    $event.preventDefault();

    // 🎯 Çift tıklama: Seçim + özel callback
    this.treeSelect.emit({ grup: grup, ctrlKey: $event.ctrlKey });

    this.grup.sahne.anaGosterim.aktifItem = grup;
    this.CD.detectChanges();
    this.FikoYT.degisti.emit();

    //@ts-ignore
    if (grup.ciftTiklandi)
      //@ts-ignore
      grup.ciftTiklandi();
  }

  itemSagTiklandi($event: any, grup: any) {
    $event.preventDefault();

    //@ts-ignore
    if (grup.sagTiklandi)
      //@ts-ignore
      grup.sagTiklandi();
  }

  /** 🎯 UZUN BAS: sagMenu olan node'da (ölçüm vb.) uzun-basışta menüyü aç (Düzenle dialoguna erişim).
   *  Aksi halde eski davranış (secililer.uzunBasildi). Touch'ta koordinat touches'tan alınır. */
  uzunBasMenu(e: any, grp: any) {
    if (grp?.sagMenu?.length) {
      const x = e?.clientX ?? e?.touches?.[0]?.clientX ?? e?.changedTouches?.[0]?.clientX ?? 0;
      const y = e?.clientY ?? e?.touches?.[0]?.clientY ?? e?.changedTouches?.[0]?.clientY ?? 0;
      this.open({ x, y } as MouseEvent, grp);
    } else {
      grp?.sahne?.anaGosterim?.secililer?.uzunBasildi?.(e, [grp]);
    }
  }

  open({ x, y }: MouseEvent, grp: any) {
    this.close();
    if (!grp) return;   // sagMenu yoksa da menü açılır (en azından "Yeniden Adlandır")

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo({ x, y })
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        }, {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        }
      ]);


    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close()
    });

    this.overlayRef.attach(new TemplatePortal(this.itemMenu, this.viewContainerRef, {
      $implicit: grp
    }));

    // 🎯 FLASH FIX: Önce görünmez yap, sonra görünür yap
    if (this.overlayRef?.overlayElement) {
      this.overlayRef.overlayElement.style.opacity = '0';
      setTimeout(() => {
        if (this.overlayRef?.overlayElement) {
          this.overlayRef.overlayElement.style.opacity = '1';
        }
      }, 20);
    }

    this.sub = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => {
          //@ts-ignore
          const clickTarget = event.target as HTMLElement;
          return !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget);
        }),
        take(1)
      ).subscribe(() => this.close())

  }

  close() {
    this.sub && this.sub.unsubscribe();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // 3D-TREE SYNC: Hover/Selection event handlers
  // ═══════════════════════════════════════════════════════════

  /**
   * Tree item hover başladığında - 3D'ye bildir
   */
  onTreeItemHover(grp: FikoGrup) {
    this.treeHover.emit(grp);
  }

  /**
   * Tree item hover bittiğinde
   */
  onTreeItemHoverEnd() {
    this.treeHover.emit(null);
  }

  /**
   * Tree item tıklandığında - 3D ile senkronize et
   */
  onTreeItemSelect(event: MouseEvent, grp: FikoGrup) {
    this.treeSelect.emit({ grup: grp, ctrlKey: event.ctrlKey });
  }

  /**
   * Parent path'i hesapla (3D seçimden tree'ye sync için utility)
   * Static method - FikoSecililer'dan çağrılabilir
   */
  static computeParentPath(obj: any): string[] {
    const path: string[] = [];
    let current = obj?.parent;
    while (current) {
      if (current.uuid) {
        path.push(current.uuid);
      }
      current = current.parent;
    }
    return path;
  }

  trackByBaslik(index: number, item: any): string {
    return item?.baslik ?? index;
  }

  trackByUuid(index: number, item: any): string {
    return item?.uuid ?? index;
  }

  trackByIsim(index: number, item: any): string {
    return item?.isim ?? item?.ismi ?? index;
  }

}
